import React from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DAY_MS = 24 * 60 * 60 * 1000;

const STATUS_STYLES = {
  scheduled: "bg-blue-500/15 text-blue-200 border-blue-500/40",
  confirmed: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40",
  in_progress: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  completed: "bg-white/10 text-white/60 border-white/20",
  cancelled: "bg-red-500/10 text-red-300/60 border-red-500/30 line-through",
  no_show: "bg-orange-500/10 text-orange-300/70 border-orange-500/30",
};

const RELEASING_STATUSES = ["cancelled", "no_show"];
// Started/completed visits can't be rescheduled either (backend 400s).
const VISIT_STARTED_STATUSES = ["in_progress", "completed"];

const statusLabel = (status) => status.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");

// Sunday at local midnight of the week containing `date`.
export const startOfWeek = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() - d.getDay());
  return d;
};

export const addDays = (date, n) => new Date(date.getTime() + n * DAY_MS);

// Local-timezone YYYY-MM-DD (not toISOString, which shifts to UTC and can
// land on the wrong calendar day for the viewer).
export const toYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const WEEKDAY_LABEL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const AppointmentsCalendar = ({
  weekStart,
  onWeekChange,
  appointments,
  patientsById,
  providersById,
  onSelect,
}) => {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const isToday = (day) => toYMD(day) === toYMD(new Date());

  const byDay = {};
  for (const day of days) byDay[toYMD(day)] = [];
  for (const appt of appointments) {
    const key = toYMD(new Date(appt.starts_at));
    if (byDay[key]) byDay[key].push(appt);
  }
  for (const key of Object.keys(byDay)) {
    byDay[key].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  }

  const weekLabel = `${weekStart.toLocaleDateString([], { month: "short", day: "numeric" })} – ${addDays(weekStart, 6).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}`;

  return (
    <div data-testid="appointments-calendar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            className="ghost-btn text-xs px-2"
            onClick={() => onWeekChange(addDays(weekStart, -7))}
            data-testid="calendar-prev-week"
          >
            <ChevronLeft size={16} />
          </Button>
          <Button
            type="button"
            className="ghost-btn text-xs"
            onClick={() => onWeekChange(startOfWeek(new Date()))}
            data-testid="calendar-today"
          >
            Today
          </Button>
          <Button
            type="button"
            className="ghost-btn text-xs px-2"
            onClick={() => onWeekChange(addDays(weekStart, 7))}
            data-testid="calendar-next-week"
          >
            <ChevronRight size={16} />
          </Button>
        </div>
        <span className="text-white/70 text-sm">{weekLabel}</span>
      </div>

      <div className="overflow-x-auto">
        <div className="grid grid-cols-7 gap-2 min-w-[840px]">
          {days.map((day) => {
            const key = toYMD(day);
            const dayAppointments = byDay[key] || [];
            return (
              <div key={key} className="min-w-[110px]">
                <div
                  className={`text-center text-xs mb-2 pb-1 border-b ${
                    isToday(day) ? "text-[#d4a62a] border-[#d4a62a]/50 font-semibold" : "text-white/60 border-white/10"
                  }`}
                >
                  {WEEKDAY_LABEL[day.getDay()]} {day.getDate()}
                </div>
                <div className="space-y-1.5 min-h-[60px]">
                  {dayAppointments.length === 0 ? (
                    <div className="text-white/30 text-xs text-center py-4">—</div>
                  ) : (
                    dayAppointments.map((appt) => {
                      const patient = patientsById[appt.patient_id];
                      const provider = providersById[appt.provider_id];
                      const style = STATUS_STYLES[appt.status] || STATUS_STYLES.scheduled;
                      const interactive = !RELEASING_STATUSES.includes(appt.status) && !VISIT_STARTED_STATUSES.includes(appt.status);
                      const Tag = interactive ? "button" : "div";
                      return (
                        <Tag
                          key={appt.id}
                          type={interactive ? "button" : undefined}
                          onClick={interactive ? () => onSelect(appt) : undefined}
                          data-testid={`calendar-appointment-${appt.id}`}
                          title={
                            appt.notes
                              ? `${statusLabel(appt.status)} — ${appt.service_name}\n${appt.notes}`
                              : `${statusLabel(appt.status)} — ${appt.service_name}`
                          }
                          className={`w-full text-left rounded border px-2 py-1 text-xs transition ${
                            interactive ? "hover:brightness-125 cursor-pointer" : "cursor-default"
                          } ${style}`}
                        >
                          <div className="font-medium">
                            {new Date(appt.starts_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                          </div>
                          <div className="truncate">
                            {patient ? `${patient.first_name} ${patient.last_name}` : appt.patient_id}
                          </div>
                          <div className="truncate opacity-75">
                            {provider ? provider.full_name : ""}{appt.service_name ? ` · ${appt.service_name}` : ""}
                          </div>
                        </Tag>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AppointmentsCalendar;
